/**
 * ComponentStore — a generic Structure-of-Arrays column store backed by a SINGLE
 * shared `ArrayBuffer` (or `SharedArrayBuffer`), addressed by offset/stride.
 *
 * This is the Phase-2 substrate that the hand-rolled `TransformStorage` /
 * `SpriteRendererStorage` (and the `storage:'soa'` fields the component codegen emits)
 * are flipped onto. One backing buffer means a column is a contiguous range that can be
 * uploaded to the GPU directly and aliased zero-copy by a C# `Span<T>` on the native
 * player (reusing the same V8-ArrayBuffer ↔ C# interop the player already uses for
 * `GPUBuffer.getMappedRange`).
 *
 * Layout is SoA-within-one-buffer: `[col0: capacity*lanes][col1: capacity*lanes]…`, each
 * column block aligned so its typed-array view is valid. Growing reallocates the buffer and
 * relocates every column block; **column views are invalidated on grow** — always re-fetch
 * via {@link ComponentStore.col} after an `allocate()` that may have grown.
 *
 * The store is per-`World` and injected (DI) — it replaces the module-global
 * `getTransformStorage()` / `getSpriteRendererStorage()` singletons.
 */

export type ColumnDType =
  | "f32"
  | "f64"
  | "i32"
  | "u32"
  | "i16"
  | "u16"
  | "i8"
  | "u8";

/** Declares one SoA column: a field of `lanes` elements of `dtype` per row. */
export interface ColumnDesc {
  /** Column name (matches the `@field` property for component-backed stores). */
  name: string;
  dtype: ColumnDType;
  /** Elements per row (1 = scalar, 2 = vec2, 3 = xyz, 4 = vec4/rgba). */
  lanes: number;
  /** Optional per-lane default written when a row is (re)allocated. */
  defaults?: readonly number[];
}

export interface ComponentStoreConfig {
  initialCapacity?: number;
  growFactor?: number;
  /** Use `SharedArrayBuffer` when available (needed for cross-thread / true zero-copy). */
  shared?: boolean;
}

interface ResolvedColumn extends ColumnDesc {
  bytesPerElem: number;
  /** Byte offset of this column's block within the buffer (for current capacity). */
  byteOffset: number;
  ctor: TypedArrayCtor;
}

type TypedArray =
  | Float32Array
  | Float64Array
  | Int32Array
  | Uint32Array
  | Int16Array
  | Uint16Array
  | Int8Array
  | Uint8Array;

type TypedArrayCtor = {
  new (buffer: ArrayBufferLike, byteOffset: number, length: number): TypedArray;
  BYTES_PER_ELEMENT: number;
};

const DTYPE_CTOR: Record<ColumnDType, TypedArrayCtor> = {
  f32: Float32Array,
  f64: Float64Array,
  i32: Int32Array,
  u32: Uint32Array,
  i16: Int16Array,
  u16: Uint16Array,
  i8: Int8Array,
  u8: Uint8Array,
};

/** Column blocks are 8-byte aligned so every typed-array view has a legal byteOffset. */
const COLUMN_ALIGN = 8;

function alignUp(n: number, a: number): number {
  return Math.ceil(n / a) * a;
}

/** A point-in-time snapshot for play-mode save/restore. */
export interface ComponentStoreSnapshot {
  buffer: ArrayBuffer;
  capacity: number;
  count: number;
  freeList: number[];
}

export class ComponentStore {
  private readonly _columns: ResolvedColumn[];
  private readonly _columnByName: Map<string, ResolvedColumn>;
  private readonly _growFactor: number;
  private readonly _shared: boolean;

  private _buffer!: ArrayBufferLike;
  private _views!: Map<string, TypedArray>;
  private _rowByteStride = 0; // total aligned bytes per row across all columns

  private _capacity = 0;
  private _count = 0;
  private _freeList: number[] = [];

  constructor(columns: readonly ColumnDesc[], config: ComponentStoreConfig = {}) {
    if (columns.length === 0) throw new Error("ComponentStore requires at least one column");

    this._growFactor = config.growFactor ?? 2;
    this._shared = config.shared ?? false;

    this._columns = columns.map((c) => {
      const ctor = DTYPE_CTOR[c.dtype];
      if (!ctor) throw new Error(`ComponentStore: unknown dtype '${c.dtype}'`);
      if (c.lanes < 1) throw new Error(`ComponentStore: column '${c.name}' needs lanes >= 1`);
      if (c.defaults && c.defaults.length !== c.lanes) {
        throw new Error(`ComponentStore: column '${c.name}' defaults length must equal lanes`);
      }
      return { ...c, ctor, bytesPerElem: ctor.BYTES_PER_ELEMENT, byteOffset: 0 };
    });
    this._columnByName = new Map(this._columns.map((c) => [c.name, c]));

    this.reallocate(config.initialCapacity ?? 256);
  }

  // ==========================================================================
  // Capacity / layout
  // ==========================================================================

  get capacity(): number {
    return this._capacity;
  }
  get count(): number {
    return this._count;
  }
  /** The single backing buffer (for GPU upload / C# Span aliasing). */
  get buffer(): ArrayBufferLike {
    return this._buffer;
  }
  /** Bytes per column block at the current capacity (aligned). Useful for ABI docs. */
  get byteLength(): number {
    return this._buffer.byteLength;
  }

  /** The current typed-array view of a column. Re-fetch after any `allocate()`. */
  col(name: string): TypedArray {
    const v = this._views.get(name);
    if (!v) throw new Error(`ComponentStore: no column '${name}'`);
    return v;
  }

  /** Byte offset of a column's block within {@link buffer} (for offset/stride ABI). */
  byteOffsetOf(name: string): number {
    const c = this._columnByName.get(name);
    if (!c) throw new Error(`ComponentStore: no column '${name}'`);
    return c.byteOffset;
  }

  private computeLayout(capacity: number): number {
    let offset = 0;
    for (const c of this._columns) {
      offset = alignUp(offset, COLUMN_ALIGN);
      c.byteOffset = offset;
      offset += capacity * c.lanes * c.bytesPerElem;
    }
    return alignUp(offset, COLUMN_ALIGN);
  }

  private makeBuffer(byteLength: number): ArrayBufferLike {
    if (this._shared && typeof SharedArrayBuffer !== "undefined") {
      return new SharedArrayBuffer(byteLength);
    }
    return new ArrayBuffer(byteLength);
  }

  private rebuildViews(): void {
    this._views = new Map();
    for (const c of this._columns) {
      this._views.set(c.name, new c.ctor(this._buffer, c.byteOffset, this._capacity * c.lanes));
    }
  }

  private reallocate(newCapacity: number): void {
    const oldColumns = this._columns.map((c) => ({ ...c })); // capture old offsets
    const oldBuffer = this._buffer;
    const oldCapacity = this._capacity;

    const totalBytes = this.computeLayout(newCapacity);
    const newBuffer = this.makeBuffer(totalBytes);

    // Relocate each column block (SoA layout shifts because per-column blocks grow).
    if (oldBuffer && oldCapacity > 0) {
      for (let i = 0; i < this._columns.length; i++) {
        const oldC = oldColumns[i];
        const newC = this._columns[i];
        const oldView = new oldC.ctor(oldBuffer, oldC.byteOffset, oldCapacity * oldC.lanes);
        const newView = new newC.ctor(newBuffer, newC.byteOffset, newCapacity * newC.lanes);
        newView.set(oldView);
      }
    }

    this._buffer = newBuffer;
    this._capacity = newCapacity;
    this.rebuildViews();
  }

  // ==========================================================================
  // Allocation
  // ==========================================================================

  /** Allocate a row (reuses freed rows). May grow the buffer — re-fetch column views after. */
  allocate(): number {
    let row: number;
    if (this._freeList.length > 0) {
      row = this._freeList.pop()!;
    } else {
      if (this._count >= this._capacity) {
        this.reallocate(Math.ceil(this._capacity * this._growFactor));
      }
      row = this._count;
    }
    this._count++;
    this.initRow(row);
    return row;
  }

  /** Free a row (its slot is reused by a later allocate). Zeroes the row's lanes. */
  free(row: number): void {
    if (!this.isValid(row)) return;
    this.zeroRow(row);
    this._freeList.push(row);
    this._count--;
  }

  isValid(row: number): boolean {
    return row >= 0 && row < this._capacity && !this._freeList.includes(row);
  }

  private initRow(row: number): void {
    for (const c of this._columns) {
      const view = this._views.get(c.name)!;
      const base = row * c.lanes;
      if (c.defaults) {
        for (let l = 0; l < c.lanes; l++) view[base + l] = c.defaults[l];
      } else {
        for (let l = 0; l < c.lanes; l++) view[base + l] = 0;
      }
    }
  }

  private zeroRow(row: number): void {
    for (const c of this._columns) {
      const view = this._views.get(c.name)!;
      const base = row * c.lanes;
      for (let l = 0; l < c.lanes; l++) view[base + l] = 0;
    }
  }

  // ==========================================================================
  // Save / restore (play-mode)
  // ==========================================================================

  saveState(): ComponentStoreSnapshot {
    // Always copy into a (non-shared) ArrayBuffer snapshot.
    const copy = new ArrayBuffer(this._buffer.byteLength);
    new Uint8Array(copy).set(new Uint8Array(this._buffer as ArrayBuffer));
    return {
      buffer: copy,
      capacity: this._capacity,
      count: this._count,
      freeList: [...this._freeList],
    };
  }

  restoreState(snapshot: ComponentStoreSnapshot): void {
    if (snapshot.capacity > this._capacity) {
      this.reallocate(snapshot.capacity);
    }
    // Restore per-column blocks from the snapshot's layout (same column order → same offsets
    // at the snapshot's capacity).
    const snapColumns = this._columns.map((c) => ({ ...c }));
    // Recompute snapshot offsets at snapshot.capacity.
    let off = 0;
    for (const c of snapColumns) {
      off = alignUp(off, COLUMN_ALIGN);
      c.byteOffset = off;
      off += snapshot.capacity * c.lanes * c.bytesPerElem;
    }
    for (let i = 0; i < this._columns.length; i++) {
      const sc = snapColumns[i];
      const dc = this._columns[i];
      const src = new sc.ctor(snapshot.buffer, sc.byteOffset, snapshot.capacity * sc.lanes);
      const dst = this._views.get(dc.name)!;
      dst.set(src);
    }
    this._count = snapshot.count;
    this._freeList = [...snapshot.freeList];
  }

  clear(): void {
    new Uint8Array(this._buffer as ArrayBuffer).fill(0);
    this._count = 0;
    this._freeList.length = 0;
  }
}
